import {
  ArrivalReceivedNotifier,
  ChatPanel,
  CloseStore,
  InboundSmsNotifier,
  NotificationPopoverStack,
  NotificationSoundNotifier,
  OpenStore,
  Page,
  TrainingListModal
} from "@ubif/components";
import { AssignedTrainingCompletionStatus, ClockTypeId, DowntimeResultPushEvent, LayoutFeature } from "@ubif/types";
import {
  addSocketEventListener,
  appcuesIdentify,
  fullStory,
  hideMoreItems,
  isFranchise,
  selectChatPanelOpen,
  selectClockInModalOpen,
  selectCurrentStore,
  selectDueTraining,
  selectIsClockedIn,
  selectIsMoreClosed,
  selectIsNavClosed,
  selectIsOnBreak,
  selectIsSearchInProgress,
  selectIsSearchSuccessOrError,
  selectIsStoreAccount,
  selectLayoutFeature,
  selectMoreMenuItems,
  selectNavMenuItems,
  selectPriceQuote,
  selectSearchArrivals,
  selectSearchCategory,
  selectSearchCriteria,
  selectSearchCriteriaRaw,
  selectSearchCustomers,
  selectSearchParts,
  selectSearchSerial,
  selectSearchWorkorders,
  selectSessionExpiry,
  selectShowSearchBox,
  selectShowSearchResults,
  selectShowTrainingModal,
  selectStoreCloseOpen,
  selectStoreLegalEntityId,
  selectStoreStatus,
  selectTrainingReminderDismissed,
  selectUser,
  setChatPanelOpen,
  setClockInModalOpen,
  setIsClockedIn,
  setIsOnBreak,
  setPartnerDowntime,
  setPrinters,
  setSaleHistoryNavItem,
  setShowPinModal,
  setShowTrainingModal,
  setStoreCloseOpenState,
  showToast,
  toggleMore,
  toggleNav,
  useBroadcastChannel,
  useBugsnag,
  useDismissDailyReminderMutation,
  useGetAssignedTrainingQuery,
  useGetPartnerDowntimeQuery,
  useGetStoreOptionsQuery,
  useGetStoreTimeSheetAccessQuery,
  useGetTechnicianClockStatusQuery,
  useGlobalDispatch,
  useLazyPinUserLogoutQuery,
  useMarkAsReadMutation,
  useResendNotificationMutation,
  useScanDetection,
  useSwitchStoreMutation,
  useTechnicianBreakReturnMutation,
  useTechnicianClockInMutation,
  useTechnicianClockOutMutation,
  useTypedSelector,
  useVisibility
} from "@ubif/util-store";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
// eslint-disable-next-line import/no-unresolved
import "dymo-connect-framework";

import { GlobalSearch } from "./GlobalSearch/GlobalSearch";
import { GlobalSearchResults } from "./GlobalSearch/GlobalSearchResults";
import { ClockInLocationModal } from "./components/ClockInLocationModal";
import { ClockAction, ConfirmClockActionModal } from "./components/ConfirmClockActionModal";
import { SalesOrderWorkorderScanListener } from "./components/SalesOrderWorkorderScan";
import { SessionTimeoutModal } from "./components/SessionTimeoutModal";
import { StoreChangeRequestPanelContainer } from "./components/StoreChangeRequestPanel/StoreChangeRequestPanelContainer";

const currentWindowId = Math.random();

declare global {
  interface Window {
    __UBIF_CHAT_USER_DATA__: {
      name: string;
      email: string;
      userId: number;
      storeId: number;
      storeName: string;
    };
  }
}
type EventMessage = {
  data: {
    type: string;
    currentWindowId: number;
    message?: string;
  };
};
export const ConnectedPage = ({ children }) => {
  const isStoreChange = useRef(false);
  const [pendingAction, setPendingAction] = useState<ClockAction | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const handleStoreChange = (eventMessage: EventMessage, windowId: number) => {
    if (eventMessage.data.type === "storeChange" && windowId !== eventMessage.data.currentWindowId) {
      isStoreChange.current = true;
    }
  };
  const handleWindowVisibility = () => {
    if (document.visibilityState === "visible" && isStoreChange.current) document.location.reload();
  };

  useVisibility(handleWindowVisibility);
  const channel = useBroadcastChannel("store-session", handleStoreChange, currentWindowId);
  const { t } = useTranslation("root-config");

  const dispatch = useGlobalDispatch();
  const navigate = useNavigate();
  const menuItems = useTypedSelector(selectNavMenuItems);
  const moreMenuItems = useTypedSelector(selectMoreMenuItems);
  const closed = useTypedSelector(selectIsNavClosed);
  const moreClosed = useTypedSelector(selectIsMoreClosed);
  const currentStore = useTypedSelector(selectCurrentStore);
  const openSearchResults = useTypedSelector(selectShowSearchResults);
  const showSearchBox = useTypedSelector(selectShowSearchBox);
  const searchCategory = useTypedSelector(selectSearchCategory);
  const searchCriteria = useTypedSelector(selectSearchCriteria);
  const searchCriteriaRaw = useTypedSelector(selectSearchCriteriaRaw);
  const isSearchInProgress = useTypedSelector(selectIsSearchInProgress);
  const isSearchSuccessOrError = useTypedSelector(selectIsSearchSuccessOrError);
  const searchedCustomersData = useTypedSelector(selectSearchCustomers);
  const searchedPartsData = useTypedSelector(selectSearchParts);
  const searchedWorkordersData = useTypedSelector(selectSearchWorkorders);
  const searchPriceQuoteData = useTypedSelector(selectPriceQuote);
  const searchedArrivalsData = useTypedSelector(selectSearchArrivals);
  const searchedSerialData = useTypedSelector(selectSearchSerial);
  const isPriceCalculatorEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.price_calculator_enabled));
  const isProgramAttributesEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.program_attributes_enabled));
  const isSaleHistoryEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.sale_history_enabled));
  const isStoreOpen = useSelector(selectStoreStatus);
  const isStoreAccount = useSelector(selectIsStoreAccount);
  const sessionExpiry = useSelector(selectSessionExpiry);
  const userDetails = useSelector(selectUser);
  const dueTraining = useSelector(selectDueTraining);
  const trainingReminderDismissed = useSelector(selectTrainingReminderDismissed);
  const [pinLogout] = useLazyPinUserLogoutQuery();
  const { data } = useGetStoreOptionsQuery();
  const { data: partnerDowntime } = useGetPartnerDowntimeQuery();
  const [switchStore] = useSwitchStoreMutation();
  const { isStoreClosePanel, isStoreOpenPanel } = useSelector(selectStoreCloseOpen);
  const isRepairOfflineFlowEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.repair_offline_flow));
  const isInAppNotificationEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.in_app_notification_enabled));
  const [dismissDailyReminder] = useDismissDailyReminderMutation();
  const [resendNotification] = useResendNotificationMutation();
  const { data: trainingList, isLoading: isTrainingListLoading } = useGetAssignedTrainingQuery(undefined, {
    skip: !dueTraining && trainingReminderDismissed
  });
  const isTrainingModalOpen = useTypedSelector(selectShowTrainingModal);
  const isChatPanelOpen = useTypedSelector(selectChatPanelOpen);
  const isDarkThemeEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.theme_dark_enabled));
  const isReleaseNotesEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.release_notes_enabled));
  const [markNotificationAsRead] = useMarkAsReadMutation();
  const isTechnicianClockInEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.technician_clock_in_enabled));
  const isIncidentAdvisorEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.incident_advisor_enabled));
  const isWaveEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.wave_enabled));
  const legalEntityId = useTypedSelector(selectStoreLegalEntityId);
  const isFranchiseStore = isFranchise(legalEntityId);
  const isClockedIn = useTypedSelector(selectIsClockedIn);
  const isOnBreak = useTypedSelector(selectIsOnBreak);
  const isClockInModalOpen = useTypedSelector(selectClockInModalOpen);
  const [clockOut] = useTechnicianClockOutMutation();
  const [clockIn] = useTechnicianClockInMutation();
  const [breakReturn] = useTechnicianBreakReturnMutation();
  const skipTechnicianClockStatus = !isTechnicianClockInEnabled || !isFranchiseStore;
  const { data: technicianClockStatus } = useGetTechnicianClockStatusQuery(currentStore.storeId, {
    skip: skipTechnicianClockStatus
  });

  useCallback(
    addSocketEventListener((evt: DowntimeResultPushEvent) => {
      if (evt?.data?.Google || evt?.data?.Samsung) {
        dispatch(setPartnerDowntime(evt.data));
      }
    }),
    []
  );

  useEffect(() => {
    fullStory((FS) => {
      FS("setIdentity", {
        uid: String(userDetails.userId),
        properties: {
          displayName: `${userDetails.givenName} ${userDetails.familyName}`,
          email: userDetails.email,
          storeId: currentStore.storeId
        }
      });
    });
    useBugsnag((Bugsnag) => {
      Bugsnag.setUser(
        String(userDetails.userId),
        `${userDetails.givenName} ${userDetails.familyName}`,
        userDetails.email
      );
      Bugsnag.addMetadata("store", { storeId: currentStore.storeId, name: currentStore.storeName });
    });

    appcuesIdentify(userDetails.userId, {
      ng_store_id: currentStore.storeId,
      ng_store_name: currentStore.storeName,
      ng_store_city: currentStore.city,
      ng_store_state: currentStore.state,
      ng_store_zip: currentStore.postalCode,
      ng_store_is_mail_in: Boolean(currentStore.mailInActive),
      ng_store_owner_group_number: String(currentStore.storeOwnerGroupNumber ?? ""),
      ng_store_time_zone: currentStore.storeTimeZone
    });

    dispatch(setPrinters(window.dymo.label.framework.getLabelWriterPrinters()));
  }, []);

  //amazon connect chat widget user data setup
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__UBIF_CHAT_USER_DATA__ = {
        name: `${userDetails.givenName || ""} ${userDetails.familyName || ""}`.trim() || "Technician",
        email: userDetails.email || "",
        userId: userDetails.userId,
        storeId: currentStore.storeId,
        storeName: currentStore.storeName || ""
      };
    }

    return () => {
      delete window.__UBIF_CHAT_USER_DATA__;
    };
  }, [userDetails, currentStore]);

  useEffect(() => {
    if (partnerDowntime) {
      dispatch(setPartnerDowntime(partnerDowntime));
    }
  }, [partnerDowntime]);

  useEffect(() => {
    if (!isTrainingListLoading && trainingList) {
      const incompleteTraining = trainingList.filter(
        (t) => t.completion_status !== AssignedTrainingCompletionStatus.Completed
      );
      if (dueTraining || (!trainingReminderDismissed && incompleteTraining.length > 0)) {
        dispatch(setShowTrainingModal(true));
      }
    }
  }, [dueTraining, trainingReminderDismissed, isTrainingListLoading, trainingList]);

  const { data: storeTimeSheetAccess } = useGetStoreTimeSheetAccessQuery(undefined);

  useEffect(() => {
    if (skipTechnicianClockStatus) {
      dispatch(setIsClockedIn(false));
      dispatch(setIsOnBreak(false));
      return;
    }
    if (technicianClockStatus !== undefined) {
      dispatch(setIsClockedIn(technicianClockStatus.isClockedIn));
      dispatch(setIsOnBreak(technicianClockStatus.isClockedIn && technicianClockStatus.clockTypeId === 2));
    }
  }, [skipTechnicianClockStatus, technicianClockStatus, dispatch]);

  useEffect(() => {
    dispatch(
      hideMoreItems({
        price_calculator_enabled: isPriceCalculatorEnabled,
        repair_offline_flow: isRepairOfflineFlowEnabled,
        technician_time_report: {
          show: Boolean(storeTimeSheetAccess?.allowed) && isTechnicianClockInEnabled,
          label: t("Nav.TechnicianTimeReport")
        },
        incident_advisor_enabled: isIncidentAdvisorEnabled,
        wave_enabled: isWaveEnabled
      })
    );
  }, [
    isPriceCalculatorEnabled,
    isProgramAttributesEnabled,
    isRepairOfflineFlowEnabled,
    storeTimeSheetAccess?.allowed,
    isIncidentAdvisorEnabled,
    isWaveEnabled,
    dispatch
  ]);

  useEffect(() => {
    dispatch(setSaleHistoryNavItem(isSaleHistoryEnabled));
  }, [isSaleHistoryEnabled, dispatch]);

  const handleTrainingModalClose = async () => {
    dispatch(setShowTrainingModal(false));
    if (!trainingReminderDismissed && !dueTraining) await dismissDailyReminder().unwrap();
  };

  const handlePanelCloseEvent = (open: boolean) => {
    if (!open) {
      dispatch(setStoreCloseOpenState({ isStoreOpenPanel: false, isStoreClosePanel: false }));
    }
  };

  const sortStoreData = (storeId: number) => {
    return [
      ...(data?.stores?.filter((item) => item.storeId === storeId) || []),
      ...(data?.stores?.filter((item) => item.storeId !== storeId) || [])
    ];
  };
  const onSwitchUser = () => {
    pinLogout().then((result) => {
      if (result?.data?.success === true) {
        dispatch(setShowPinModal(true));
      }
    });
  };

  const handleClockButtonClick = () => {
    if (isClockedIn) {
      setPendingAction(ClockAction.ClockOut);
    } else {
      dispatch(setClockInModalOpen(true));
    }
  };

  const handleBreakButtonClick = () => {
    setPendingAction(isOnBreak ? ClockAction.ReturnFromBreak : ClockAction.GoOnBreak);
  };

  const handleConfirmClockAction = async () => {
    setIsConfirming(true);
    try {
      switch (pendingAction) {
        case ClockAction.ClockOut:
          await clockOut().unwrap();
          dispatch(setIsClockedIn(false));
          dispatch(setIsOnBreak(false));
          break;
        case ClockAction.ReturnFromBreak:
          await breakReturn().unwrap();
          dispatch(setIsOnBreak(false));
          break;
        case ClockAction.GoOnBreak:
          await clockIn({ location: "IN_STORE", clockTypeId: ClockTypeId.Break }).unwrap();
          dispatch(setIsOnBreak(true));
          break;
      }
    } catch {
      const message = pendingAction === ClockAction.ClockOut ? t("ClockOutError") : t("BreakError");
      dispatch(showToast({ toastType: "error", message, time: 3000 }));
    } finally {
      setIsConfirming(false);
      setPendingAction(null);
    }
  };

  const onStoreChange = (newStore: string, newStoreName: string) => {
    switchStore(newStore)
      .unwrap()
      .then((result) => {
        if (result?.success) {
          const message = {
            type: "storeChange",
            message: "current store has been change",
            currentWindowId
          };
          channel.postMessage(message);
          dispatch(
            showToast({ toastType: "success", message: t("NewStoreMessage", { storeName: newStoreName }), time: 1000 })
          );
          if (result?.isRemoteTechEnabled === false) {
            document.location.href = "/";
          } else {
            document.location.reload();
          }
        }
      });
  };

  useScanDetection();

  return (
    <>
      {isTrainingModalOpen && !isTrainingListLoading && trainingList && (
        <TrainingListModal
          trainingList={trainingList}
          isOpen={isTrainingModalOpen}
          onClose={handleTrainingModalClose}
        />
      )}
      {isStoreOpenPanel && <OpenStore isPanelOpen={true} handlePanelCloseEvent={handlePanelCloseEvent} />}
      {isStoreClosePanel && <CloseStore isPanelOpen={true} handlePanelCloseEvent={handlePanelCloseEvent} />}
      <Page
        chatPanelOpen={isChatPanelOpen}
        nav={{
          items: menuItems,
          closed,
          onToggle: () => dispatch(toggleNav()),
          moreItems: moreMenuItems,
          moreClosed,
          onToggleMore: () => dispatch(toggleMore()),
          bottomItem: isReleaseNotesEnabled
            ? {
                icon: "files-notes/file-notes-document.svg",
                label: t("ReleaseNotes.navLabel"),
                to: "/release-notes"
              }
            : undefined
        }}
        header={{
          currentUser: userDetails,
          currentStore: currentStore.storeName,
          currentStoreId: currentStore.storeId,
          isStoreOpen: isStoreOpen,
          storeOptions: {
            stores: sortStoreData(userDetails.defaultStoreId)
          },
          onStoreChange: onStoreChange,
          onStoreButtonClick: () => {
            dispatch(
              setStoreCloseOpenState({
                isStoreOpenPanel: !isStoreOpen,
                isStoreClosePanel: isStoreOpen
              })
            );
          },
          onSwitchUser: isStoreAccount ? onSwitchUser : undefined,
          sessionExpiry,
          searchComponent: (
            <GlobalSearch
              showSearchBox={showSearchBox}
              searchCategory={searchCategory}
              searchCriteria={searchCriteria}
              searchCriteriaRaw={searchCriteriaRaw}
              isSearchInProgress={isSearchInProgress}
              searchedCustomersData={searchedCustomersData}
              searchedWorkordersData={searchedWorkordersData}
              searchedArrivalsData={searchedArrivalsData}
              searchedPartsData={searchedPartsData}
              searchPriceQuote={searchPriceQuoteData}
              searchedSerialData={searchedSerialData}
            />
          ),
          onLogoClick: () => {
            navigate("/");
          },
          onChatPanelToggle: () => {
            dispatch(setChatPanelOpen(!isChatPanelOpen));
          },
          isDarkThemeEnabled,
          clockInEnabled: isTechnicianClockInEnabled && isFranchiseStore,
          isClockedIn,
          isOnBreak,
          onClockButtonClick: handleClockButtonClick,
          onBreakButtonClick: handleBreakButtonClick
        }}
        searchResultComponent={
          <GlobalSearchResults
            navClosed={closed}
            moreClosed={moreClosed}
            openSearchResults={openSearchResults}
            searchCriteriaRaw={searchCriteriaRaw}
            isSearchInProgress={isSearchInProgress}
            isSearchSuccessOrError={isSearchSuccessOrError}
            searchedCustomersData={searchedCustomersData}
            searchedWorkorderData={searchedWorkordersData}
          />
        }
      >
        {children}
      </Page>
      {isChatPanelOpen && (
        <SidePanel>
          <ChatPanel />
        </SidePanel>
      )}
      <StoreChangeRequestPanelContainer />
      <SessionTimeoutModal onSwitchUser={onSwitchUser} expiry={sessionExpiry} isStoreAccount={isStoreAccount} />
      {isClockInModalOpen && <ClockInLocationModal />}
      {pendingAction && (
        <ConfirmClockActionModal
          action={pendingAction}
          isLoading={isConfirming}
          onConfirm={handleConfirmClockAction}
          onCancel={() => setPendingAction(null)}
        />
      )}
      <ArrivalReceivedNotifier />
      <InboundSmsNotifier />
      <SalesOrderWorkorderScanListener />
      <NotificationSoundNotifier />
      {isInAppNotificationEnabled && (
        <NotificationPopoverStack
          storeId={currentStore.storeId}
          onMarkAsRead={(notificationId) => {
            markNotificationAsRead({ notificationId, storeId: currentStore.storeId })
              .unwrap()
              .catch(() => {
                showToast({ toastType: "error", message: t("ErrorMarkingNotificationAsRead") });
              });
          }}
          onSnoozeExpireResend={(notificationId) =>
            resendNotification({ notificationId, userId: userDetails.userId }).unwrap()
          }
        />
      )}
    </>
  );
};

const SidePanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 32rem;
  height: 100vh;
  background: var(--aui-surface, #fff);
  border-left: 1px solid var(--aui-outline, #e0e0e0);
  box-shadow: -2px 0 8px var(--aui-fills-drop-shadow);
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 2001;
  padding: 0;
  transition: transform 0.3s ease-in-out;
`;
